import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import LeadCaptureModal from '../../components/feature/LeadCaptureModal';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

export default function PlanosPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [faqs, setFaqs] = useState<Array<{ question: string; answer: string }>>([]);

  useEffect(() => {
    const fetchFAQs = async () => {
      const { data, error } = await supabase
        .from('faqs')
        .select('question, answer')
        .eq('category', 'Planos e Preços')
        .eq('is_published', true)
        .order('order_position', { ascending: true });

      if (!error && data) {
        setFaqs(data);
      }
    };

    fetchFAQs();
  }, []);

  const plans = [
    {
      name: 'Básico',
      price: 'R$ 59,90',
      period: '/mês',
      description: 'Transportadoras que já estão iniciando estruturadas',
      limit: 'Limitado a 10 CT-e',
      features: [
        'Cadastros',
        'Controle de estoque',
        'Emissor de CT-e integrado com SEFAZ',
        'Cadastro de motoristas',
        'SPED Fiscal ICMS',
        'Suporte especializado',
      ],
      highlighted: false,
    },
    {
      name: 'Essencial',
      price: 'R$ 129,90',
      period: '/mês',
      description: 'Transportadora em crescimento com gestão completa',
      limit: 'Emissão de CT-e e MDF-e ilimitada',
      features: [
        'Tudo do Básico',
        'Emissão ilimitada de documentos fiscais',
        'Entrada documentos por XML',
      ],
      highlighted: true,
      badge: 'Mais Popular',
    },
    {
      name: 'Gestão',
      price: 'R$ 169,90',
      period: '/mês',
      description: 'Transportadora que precisa de controle financeiro completo além do operacional',
      limit: 'Emissão de CT-e e MDF-e ilimitada',
      features: [
        'Tudo do Essencial',
        'Controle financeiro (contas a pagar e receber)',
        'Fluxo de caixa',
        'Conciliação financeira',
      ],
      highlighted: false,
    },
    {
      name: 'Personalizado',
      price: 'Monte seu combo',
      period: '',
      description: 'Quem quer adicionar recursos específicos ao Essencial ou Gestão',
      limit: 'Adicione apenas o que você precisa',
      features: [
        'Emissão de Boletos - cobrança facilitada',
        'SPED Contribuições (Pis e Cofins)',
        'Pedido de compra',
        'Integração com PIX',
        'Integração com TEF',
      ],
      highlighted: false,
      customLabel: 'Módulos adicionais disponíveis:',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 sm:pt-28 lg:pt-32 pb-12 sm:pb-16 lg:pb-20 bg-gradient-to-br from-primary via-primary-700 to-primary-900 text-white">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Breadcrumb
            items={[
              { label: 'Início', href: '/' },
              { label: 'Planos e Preços' },
            ]}
          />
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">
            Planos do Sistema para Transportadora
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-neutral-100 max-w-3xl mx-auto leading-relaxed px-2">
            Escolha o plano ideal do nosso <strong>software para transportadora</strong> de acordo com as necessidades do seu negócio
          </p>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 mb-3 sm:mb-4">
              Software para Transportadora com Planos Flexíveis
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-neutral-700 max-w-4xl mx-auto leading-relaxed px-2">
              Nosso <strong>sistema para transportadora</strong> oferece planos flexíveis com <strong>emissão de CT-e</strong> integrada à SEFAZ. 
              Seja você uma <strong>transportadora pequena</strong> iniciando ou uma empresa em crescimento, 
              temos o plano ideal com <strong>software de gestão de transportes</strong> e controle operacional completo.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-neutral-50">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl p-5 sm:p-6 lg:p-8 transition-all ${
                  plan.highlighted
                    ? 'border-2 border-secondary shadow-hover lg:scale-105 order-first sm:order-none'
                    : 'border border-neutral-200 shadow-card hover:shadow-hover'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2">
                    <span className="px-3 sm:px-4 py-1 bg-secondary text-white text-xs sm:text-sm font-semibold rounded-full whitespace-nowrap">
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className="text-center mb-4 sm:mb-6">
                  <h3 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-2">{plan.name}</h3>
                  <p className="text-xs sm:text-sm text-neutral-600 mb-3 sm:mb-4 min-h-[40px] sm:min-h-[48px]">{plan.description}</p>
                  <div className="flex items-end justify-center mb-2">
                    <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900">{plan.price}</span>
                    {plan.period && <span className="text-neutral-600 ml-1 text-sm">{plan.period}</span>}
                  </div>
                  {plan.limit && (
                    <p className="text-xs text-secondary font-semibold">{plan.limit}</p>
                  )}
                </div>

                <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                  {plan.customLabel && (
                    <li className="text-xs sm:text-sm font-semibold text-neutral-800 mb-2">
                      {plan.customLabel}
                    </li>
                  )}
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-xs sm:text-sm text-neutral-700">
                      <i className="ri-check-line text-secondary mr-2 mt-0.5 flex-shrink-0"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => setIsModalOpen(true)}
                  className={`w-full py-2.5 sm:py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base ${
                    plan.highlighted
                      ? 'bg-secondary text-white hover:bg-secondary-600 shadow-soft'
                      : 'bg-neutral-100 text-neutral-900 hover:bg-neutral-200'
                  }`}
                >
                  Falar com consultor
                </button>
              </div>
            ))}
          </div>

          <div className="mt-10 sm:mt-16 text-center">
            <p className="text-neutral-600 mb-4 text-sm sm:text-base px-2">
              Todos os planos incluem 14 dias de teste gratuito • Atualizações automáticas
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-xs sm:text-sm text-neutral-700">
              <div className="flex items-center">
                <i className="ri-shield-check-line text-secondary mr-2"></i>
                <span>Emissor de CT-e certificado</span>
              </div>
              <div className="flex items-center">
                <i className="ri-refresh-line text-secondary mr-2"></i>
                <span>Atualizações gratuitas</span>
              </div>
              <div className="flex items-center">
                <i className="ri-customer-service-2-line text-secondary mr-2"></i>
                <span>Suporte especializado</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Implantação Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-6 sm:p-8 lg:p-10 border border-primary/10">
            <div className="text-center mb-6 sm:mb-8">
              <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-primary/10 rounded-full mx-auto mb-4">
                <i className="ri-user-star-line text-2xl sm:text-3xl text-primary"></i>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 mb-3 sm:mb-4">
                Implantação Guiada e Personalizada
              </h2>
            </div>
            <p className="text-sm sm:text-base lg:text-lg text-neutral-700 leading-relaxed text-center max-w-3xl mx-auto mb-6">
              Cada cliente conta com o acompanhamento dedicado de um <strong>consultor especializado</strong> durante todo o processo de implantação, 
              garantindo configuração adequada, treinamento completo da equipe e adoção efetiva do sistema no dia a dia do seu negócio.
            </p>
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-soft">
              <div className="flex items-center justify-center gap-2 text-secondary">
                <i className="ri-customer-service-2-line text-xl sm:text-2xl"></i>
                <p className="text-sm sm:text-base font-semibold text-neutral-800">
                  Após implantação o cliente conta com <strong>Suporte Especializado</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="py-16 sm:py-20 lg:py-24 bg-neutral-50">
          <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-3 sm:mb-4">
                Perguntas Frequentes
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-neutral-600 px-2">
                Tire suas dúvidas sobre nosso <strong>software para transportadora</strong>
              </p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl overflow-hidden border border-neutral-200"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left hover:bg-neutral-50 transition-colors cursor-pointer"
                  >
                    <span className="font-semibold text-neutral-900 pr-4 text-sm sm:text-base">{faq.question}</span>
                    <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-xl sm:text-2xl text-neutral-600 flex-shrink-0`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-5">
                      <p className="text-neutral-700 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WhatsApp CTA */}
      <section className="py-12 sm:py-16 bg-green-50">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between bg-white p-6 sm:p-8 rounded-2xl shadow-card gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-2">
                Dúvidas sobre o Sistema?
              </h3>
              <p className="text-neutral-600 text-sm sm:text-base">
                Fale com nossa equipe pelo WhatsApp
              </p>
            </div>
            <a
              href="https://wa.me/5535998093635?text=Olá,%20tenho%20dúvidas%20sobre%20o%20sistema%20para%20transportadora%20TG%20WEB"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-full md:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-all shadow-soft hover:shadow-hover whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl sm:text-2xl mr-2 sm:mr-3"></i>
              Falar no WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-secondary via-secondary-500 to-secondary-600 text-white">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">
            Comece a Usar o Sistema TG WEB
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-white/90 mb-6 sm:mb-10 leading-relaxed px-2">
            Teste grátis por 14 dias nosso <strong>software de gestão de transportes</strong>. 
            Sem cartão de crédito. Sem compromisso.
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white text-secondary text-base sm:text-lg font-semibold rounded-lg hover:bg-neutral-100 transition-all shadow-hover whitespace-nowrap cursor-pointer"
          >
            Falar com consultor
          </button>
        </div>
      </section>

      {/* Related Links Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-6 sm:mb-8 text-center">
            Explore Mais Sobre o TG WEB
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <Link
              to="/funcionalidades"
              className="group p-4 sm:p-6 bg-neutral-50 rounded-xl hover:bg-primary/5 transition-colors border border-neutral-100"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-primary/10 rounded-lg mb-2 sm:mb-3 group-hover:bg-primary/20 transition-colors">
                <i className="ri-apps-line text-lg sm:text-xl text-primary"></i>
              </div>
              <h4 className="font-semibold text-neutral-900 mb-1 group-hover:text-primary transition-colors text-sm sm:text-base">
                Funcionalidades
              </h4>
              <p className="text-xs sm:text-sm text-neutral-600 hidden sm:block">
                Conheça todos os recursos
              </p>
            </Link>

            <Link
              to="/sistema-emissao-cte"
              className="group p-4 sm:p-6 bg-neutral-50 rounded-xl hover:bg-primary/5 transition-colors border border-neutral-100"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-secondary/10 rounded-lg mb-2 sm:mb-3 group-hover:bg-secondary/20 transition-colors">
                <i className="ri-file-text-line text-lg sm:text-xl text-secondary"></i>
              </div>
              <h4 className="font-semibold text-neutral-900 mb-1 group-hover:text-primary transition-colors text-sm sm:text-base">
                Emissor de CT-e
              </h4>
              <p className="text-xs sm:text-sm text-neutral-600 hidden sm:block">
                Emita CT-e e MDF-e
              </p>
            </Link>

            <Link
              to="/faq"
              className="group p-4 sm:p-6 bg-neutral-50 rounded-xl hover:bg-primary/5 transition-colors border border-neutral-100"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-primary/10 rounded-lg mb-2 sm:mb-3 group-hover:bg-primary/20 transition-colors">
                <i className="ri-question-answer-line text-lg sm:text-xl text-primary"></i>
              </div>
              <h4 className="font-semibold text-neutral-900 mb-1 group-hover:text-primary transition-colors text-sm sm:text-base">
                FAQ
              </h4>
              <p className="text-xs sm:text-sm text-neutral-600 hidden sm:block">
                Tire suas dúvidas
              </p>
            </Link>

            <Link
              to="/blog"
              className="group p-4 sm:p-6 bg-neutral-50 rounded-xl hover:bg-primary/5 transition-colors border border-neutral-100"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-secondary/10 rounded-lg mb-2 sm:mb-3 group-hover:bg-secondary/20 transition-colors">
                <i className="ri-article-line text-lg sm:text-xl text-secondary"></i>
              </div>
              <h4 className="font-semibold text-neutral-900 mb-1 group-hover:text-primary transition-colors text-sm sm:text-base">
                Blog
              </h4>
              <p className="text-xs sm:text-sm text-neutral-600 hidden sm:block">
                Conteúdo sobre gestão
              </p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <LeadCaptureModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
